
import React from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ChartDataLabels);

interface PaceReportData {
  Title?: string;
  report?: Array<{
    propertyId?: number;
    propertyName?: string;
    current?: Record<string, number>;
    previous?: Record<string, number>;
  }>;
}

const PaceReportBarChart = ({ data }: { data: PaceReportData }) => {
  // Extract the report data
  const { Title: reportTitle, report = [] } = data || {};

  if (!report || !Array.isArray(report) || report.length === 0) {
    return (
      <div className="flex items-center justify-center h-40">
        <p className="text-muted-foreground">No data available to display</p>
      </div>
    );
  }

  const charts = report.map((property, index) => {
    const { propertyName = 'Unknown Property', current = {}, previous = {} } = property || {};

    // Define datasets for each chart
    const datasets = [
      {
        label: 'Reservations & Nights',
        labels: ['Reservations', 'Nights'],
        currentData: [current.reservation || 0, current.nights || 0],
        previousData: [previous.reservation || 0, previous.nights || 0],
      },
      {
        label: 'Gross & Net Revenue',
        labels: ['Gross Revenue', 'Nett Revenue'],
        currentData: [current.grossRevenue || 0, current.nettRevenue || 0],
        previousData: [previous.grossRevenue || 0, previous.nettRevenue || 0],
      },
      {
        label: 'Average Daily Rates',
        labels: ['Gross ADR', 'Nett ADR'],
        currentData: [current.averageDailyRateGross || 0, current.averageDailyRateNett || 0],
        previousData: [previous.averageDailyRateGross || 0, previous.averageDailyRateNett || 0],
      },
      {
        label: 'Occupancy Percentage',
        labels: ['Occupancy %'],
        currentData: [current.occupancyPercent || 0],
        previousData: [previous.occupancyPercent || 0],
      },
    ];

    return (
      datasets.map((dataset, chartIndex) => {
      const chartData = {
        labels: dataset.labels,
        datasets: [
          {
            label: 'Current',
            data: dataset.currentData,
            backgroundColor: 'rgba(24, 144, 255, 0.6)',
            borderColor: 'rgba(24, 144, 255, 1)',
            borderWidth: 1,
          },
          {
            label: 'Previous',
            data: dataset.previousData,
            backgroundColor: 'rgba(245, 34, 45, 0.6)',
            borderColor: 'rgba(245, 34, 45, 1)',
            borderWidth: 1,
          },
        ],
      };

      const options = {
        borderRadius: 4,
        responsive: true,
        plugins: {
          title: {
            display: true,
            text: `${dataset.label}`,
            font: {
              size: 18,
              weight: 'bold' as const
            },
            padding: {
              top: 10,
              bottom: 20
            }
          },
          legend: {
            position: 'top' as const,
            labels: {
              font: {
                size: 12
              }
            }
          },
          datalabels: {
            color: '#333',
            font: {
              weight: 'bold' as const,
              size: 12
            },
            formatter: function(value: any, context: any) {
              if (value === undefined || value === null) return '';
              
              const addPrefixSuffix = (value: any) => {
                if (context.chart.data.labels[0].includes('Gross') || context.chart.data.labels[0].includes('Nett')) {
                  return `$${Number(value).toLocaleString()}`;
                } else if (context.chart.data.labels[0].includes('%')) {
                  return `${Number(value).toFixed(2)}%`;
                }
                return value;
              }
              return addPrefixSuffix(value);
            },
            // Fixed format for chartjs-plugin-datalabels
            labels: {
              title: {
                font: {
                  weight: 'bold' as const
                }
              },
              value: {
                color: '#333'
              }
            }
          }
        },
        scales: {
          x: {
            title: {
              display: true,
              text: 'Metrics',
              font: {
                size: 14
              }
            },
            grid: {
              display: false
            }
          },
          y: {
            title: {
              display: true,
              text: 'Values',
              font: {
                size: 14
              }
            },
            grid: {
              color: 'rgba(0, 0, 0, 0.1)'
            }
          },
        },
      };

      return (
        <div key={`${index}-${chartIndex}`} className="mb-8 h-[400px] flex justify-center items-center">  
          <Bar data={chartData} options={options} />
        </div>
      );
    }));
  });

  return (
    <div className="space-y-4">
      {charts.flat()}
    </div>
  );
};

export default React.memo(PaceReportBarChart);
